import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { MassRecipientInput } from '#/shared/api/generated/communication/models'
import { useMassEmailPreview, useMassSmsPreview } from '#/shared/lib/mass-jobs'
import type { MassPreviewResult } from '#/shared/lib/mass-jobs'
import { cn } from '#/shared/lib/utils'
import {
  buildMassRecipientsLabels,
  REMOVED_REASON_DOT_CLASS,
} from '#/shared/ui/mass-recipients'
import type { MassRecipientsChannelNs } from '#/shared/ui/mass-recipients'
import { SmsDeliverabilityNotice } from '#/shared/ui/sms-segment-counter'

/** In the editor header the bar sits between px-2.5 rows; in the compact layout
 *  it floats between cards, so it becomes a card itself. */
function barClass(compact: boolean | undefined): string {
  return compact
    ? 'rounded-lg border border-gray-200 px-3 py-2.5'
    : 'border-b border-gray-200 px-2.5 py-2'
}

function toRecipients(contacts: string[]): MassRecipientInput[] {
  return contacts.map((contact) => ({ contact }))
}

/** A single-shipment composer only ever targets one recipient (already shown
 *  in the To field), so a "0 of 1" count and repeating the number are both
 *  noise. When it's filtered out we just say it can't be sent, and why. */
function ReasonBar({
  ns,
  preview,
  compact,
}: {
  ns: MassRecipientsChannelNs
  preview: MassPreviewResult
  compact?: boolean
}) {
  const { t } = useTranslation()
  const labels = buildMassRecipientsLabels(t, ns, {
    totalSelected: preview.totalSelected,
    willReceive: preview.recipientCount,
    removedCount: preview.removedCount,
  })
  const [dropped] = preview.removed
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2.5 bg-gray-50 text-sm',
        barClass(compact),
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span
          className={cn(
            'size-1.75 shrink-0 rounded-full',
            REMOVED_REASON_DOT_CLASS[dropped.reason],
          )}
        />
        <p className="truncate text-gray-900">{t(`${ns}.summary.single`)}</p>
      </div>
      <p className="shrink-0 text-gray-500">{labels.reason(dropped)}</p>
    </div>
  )
}

/** Deliverability preview for the SMS composer — same server-side curation
 *  (invalid / do-not-text / unsubscribed) and body lint the mass dialog runs,
 *  scoped to the selected sender line. Renders only what it has: the reason bar
 *  when the recipient is dropped, the notice when the body has findings. */
export function SmsRecipientsSummary({
  contacts,
  fromPhoneId,
  message,
  compact,
}: {
  contacts: string[]
  fromPhoneId: string
  /** Plain-text body, linted for the signals that get a message spam-blocked. */
  message?: string
  compact?: boolean
}) {
  const recipients = useMemo(() => toRecipients(contacts), [contacts])
  const preview = useMassSmsPreview(recipients, fromPhoneId, { message })
  const { findings } = preview.deliverability
  if (contacts.length === 0) return null
  const dropped = preview.removedCount > 0
  if (!dropped && findings.length === 0) return null
  return (
    <div className={cn('flex flex-col', compact ? 'gap-2' : 'gap-1.5')}>
      {dropped && (
        <ReasonBar ns="massSms" preview={preview} compact={compact} />
      )}
      {/* Blockers stay out of this list: a dropped recipient is already spelled
          out by the reason bar above, and this composer doesn't gate Send. */}
      <SmsDeliverabilityNotice
        findings={findings}
        className={compact ? 'px-0.5' : 'px-2.5 pb-1'}
      />
    </div>
  )
}

/** Same as {@link SmsRecipientsSummary} for the email composer — the server
 *  also drops unsubscribed addresses. */
export function EmailRecipientsSummary({
  contacts,
  compact,
}: {
  contacts: string[]
  compact?: boolean
}) {
  const recipients = useMemo(() => toRecipients(contacts), [contacts])
  const preview = useMassEmailPreview(recipients)
  if (contacts.length === 0 || preview.removedCount === 0) return null
  return <ReasonBar ns="massEmail" preview={preview} compact={compact} />
}
